import type { ReactNode } from "react";
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
  type TextInputProps,
} from "react-native";

import { HIT_SLOP, colour, elevation, radius, space, type } from "./theme";

/**
 * The controls (WI-045, WI-046).
 *
 * WI-045 made there be one of each. WI-046 gave them a look: warm neutrals, a
 * card-table green for anything that acts, real line heights, and cards that
 * sit on the page rather than being drawn on it.
 *
 * No icons and no web font -- the bundle had 48KB spare. Everything here is
 * type, colour, space and shape.
 */

// ---------------------------------------------------------------- layout ---

export function Screen({ children }: { children: ReactNode }) {
  return <View style={styles.screen}>{children}</View>;
}

export function Card({ children }: { children: ReactNode }) {
  return <View style={styles.card}>{children}</View>;
}

export function CardHead({ children }: { children: ReactNode }) {
  return <View style={styles.cardHead}>{children}</View>;
}

/**
 * Where actions go.
 *
 * **Primary first, destructive last, always.** A Delete that is second on one
 * screen and fourth on another is how somebody taps it by accident, and taking
 * `destructive` separately means a screen cannot quietly put it elsewhere.
 */
export function ActionRow({
  children,
  destructive,
}: {
  children: ReactNode;
  destructive?: ReactNode;
}) {
  return (
    <View style={styles.actions}>
      <View style={styles.actionsMain}>{children}</View>
      {destructive}
    </View>
  );
}

/** A hairline between sections of a card. */
export function Divider() {
  return <View style={styles.divider} />;
}

// ------------------------------------------------------------------ text ---

export const Display = ({ children }: { children: ReactNode }) => (
  <Text style={styles.display}>{children}</Text>
);

export const Title = ({ children }: { children: ReactNode }) => (
  <Text style={styles.title}>{children}</Text>
);

export const Body = ({ children }: { children: ReactNode }) => (
  <Text style={styles.body}>{children}</Text>
);

export const Meta = ({ children }: { children: ReactNode }) => (
  <Text style={styles.meta}>{children}</Text>
);

export const Label = ({ children }: { children: ReactNode }) => (
  <Text style={styles.label}>{children}</Text>
);

export const Hint = ({ children }: { children: ReactNode }) => (
  <Text style={styles.hint}>{children}</Text>
);

/** A failure, in the same place and colour every time. */
export function ErrorText({ children }: { children?: string | null }) {
  if (!children) return null;
  return (
    <View style={styles.errorBox}>
      <Text style={styles.errorText}>{children}</Text>
    </View>
  );
}

/** Nothing here yet, said the same way everywhere. */
export function Empty({ children }: { children: ReactNode }) {
  return <Text style={styles.empty}>{children}</Text>;
}

// --------------------------------------------------------------- buttons ---

type Variant = "primary" | "secondary" | "danger";

export function Button({
  label,
  onPress,
  variant = "secondary",
  busy = false,
  disabled = false,
}: {
  label: string;
  onPress: () => void;
  variant?: Variant;
  busy?: boolean;
  disabled?: boolean;
}) {
  const isPrimary = variant === "primary";
  const off = disabled || busy;

  return (
    <Pressable
      onPress={onPress}
      disabled={off}
      hitSlop={HIT_SLOP}
      accessibilityRole="button"
      style={({ pressed }) => [
        isPrimary ? styles.primary : styles.plain,
        isPrimary && pressed && styles.primaryPressed,
        off && styles.off,
      ]}
    >
      {busy ? (
        <ActivityIndicator color={isPrimary ? colour.onAccent : colour.ink} />
      ) : (
        <Text
          style={[
            isPrimary && styles.primaryLabel,
            variant === "secondary" && styles.secondaryLabel,
            variant === "danger" && styles.dangerLabel,
          ]}
        >
          {label}
        </Text>
      )}
    </Pressable>
  );
}

// ----------------------------------------------------------------- chips ---

export function ChipRow({ children }: { children: ReactNode }) {
  return <View style={styles.chips}>{children}</View>;
}

export function Chip({
  label,
  selected = false,
  tone = "default",
  onPress,
}: {
  label: string;
  selected?: boolean;
  tone?: "default" | "win";
  onPress: () => void;
}) {
  return (
    <Pressable
      onPress={onPress}
      hitSlop={HIT_SLOP}
      accessibilityRole="button"
      accessibilityState={{ selected }}
      style={[styles.chip, selected && (tone === "win" ? styles.chipWin : styles.chipOn)]}
    >
      <Text style={[styles.chipLabel, selected && styles.chipLabelOn]}>{label}</Text>
    </Pressable>
  );
}

/** A read-only badge. Says something about a thing rather than doing something. */
export function Badge({ label, tone = "default" }: { label: string; tone?: "default" | "win" }) {
  return (
    <View style={[styles.badge, tone === "win" && styles.badgeWin]}>
      <Text style={[styles.badgeLabel, tone === "win" && styles.badgeLabelWin]}>{label}</Text>
    </View>
  );
}

// ----------------------------------------------------------------- input ---

export function Field(props: TextInputProps) {
  return (
    <TextInput
      {...props}
      placeholderTextColor={colour.inkFaint}
      style={[styles.field, props.multiline === true && styles.fieldTall, props.style]}
    />
  );
}

/** One to ten, tap again to clear. */
export function RatingPicker({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (next: number | null) => void;
}) {
  return (
    <View style={styles.chips}>
      {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
        <Pressable
          key={n}
          onPress={() => onChange(value === n ? null : n)}
          accessibilityRole="button"
          accessibilityState={{ selected: value === n }}
          style={[styles.rating, value === n && styles.ratingOn]}
        >
          <Text style={[styles.ratingLabel, value === n && styles.ratingLabelOn]}>{n}</Text>
        </Pressable>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, padding: space.lg, gap: space.md, backgroundColor: colour.canvas },

  card: {
    backgroundColor: colour.surface,
    borderRadius: radius.lg,
    borderWidth: 1,
    borderColor: colour.line,
    padding: space.lg,
    gap: space.sm,
    ...elevation,
  },
  cardHead: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: space.sm,
  },
  divider: { height: 1, backgroundColor: colour.line, marginVertical: space.sm },

  display: { ...type.display, color: colour.ink },
  title: { ...type.title, color: colour.ink, flexShrink: 1 },
  body: { ...type.body, color: colour.ink },
  meta: { ...type.meta, color: colour.inkMuted },
  label: { ...type.label, color: colour.inkFaint, marginTop: space.sm },
  hint: { ...type.meta, color: colour.inkFaint },
  empty: { ...type.body, color: colour.inkMuted, textAlign: "center" },

  errorBox: {
    backgroundColor: colour.dangerSurface,
    borderColor: colour.dangerLine,
    borderWidth: 1,
    borderRadius: radius.sm,
    paddingVertical: space.sm,
    paddingHorizontal: space.md,
  },
  errorText: { ...type.meta, color: colour.danger },

  actions: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: space.lg,
    marginTop: space.sm,
  },
  actionsMain: { flexDirection: "row", alignItems: "center", gap: space.lg },

  primary: {
    backgroundColor: colour.accent,
    borderRadius: radius.md,
    paddingVertical: space.md,
    paddingHorizontal: space.xl,
    minWidth: 132,
    alignItems: "center",
  },
  primaryPressed: { backgroundColor: colour.accentPressed },
  primaryLabel: { ...type.title, color: colour.onAccent, letterSpacing: 0 },
  plain: { paddingVertical: space.xs },
  secondaryLabel: { ...type.meta, fontWeight: "600", color: colour.accent },
  dangerLabel: { ...type.meta, fontWeight: "600", color: colour.danger },
  off: { opacity: 0.5 },

  chips: { flexDirection: "row", flexWrap: "wrap", gap: space.sm },
  chip: {
    borderWidth: 1,
    borderColor: colour.lineStrong,
    borderRadius: radius.pill,
    paddingVertical: space.sm,
    paddingHorizontal: space.md,
    backgroundColor: colour.sunken,
  },
  chipOn: { backgroundColor: colour.accent, borderColor: colour.accent },
  chipWin: { backgroundColor: colour.win, borderColor: colour.win },
  chipLabel: { ...type.meta, color: colour.ink },
  chipLabelOn: { color: colour.onAccent, fontWeight: "600" },

  badge: {
    alignSelf: "flex-start",
    borderRadius: radius.pill,
    backgroundColor: colour.sunken,
    paddingVertical: space.xs,
    paddingHorizontal: space.sm,
  },
  badgeWin: { backgroundColor: colour.win },
  badgeLabel: { ...type.label, color: colour.inkMuted },
  badgeLabelWin: { color: colour.onInk },

  rating: {
    borderWidth: 1,
    borderColor: colour.lineStrong,
    borderRadius: radius.sm,
    width: 38,
    height: 38,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colour.sunken,
  },
  ratingOn: { backgroundColor: colour.accent, borderColor: colour.accent },
  ratingLabel: { ...type.meta, color: colour.ink },
  ratingLabelOn: { color: colour.onAccent, fontWeight: "700" },

  field: {
    backgroundColor: colour.sunken,
    borderColor: colour.lineStrong,
    borderWidth: 1,
    borderRadius: radius.md,
    paddingHorizontal: space.md,
    paddingVertical: space.md,
    ...type.body,
    color: colour.ink,
  },
  fieldTall: { minHeight: 76, textAlignVertical: "top" },
});
